/**
 * API client.
 *
 * Same-origin in development via the Vite proxy, so the session cookie is sent automatically
 * and there is no token in localStorage to steal (SECURITY.md 2).
 */

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    /** True when the server refused because the kill switch is engaged. */
    public readonly killSwitch = false,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: {
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });

  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`;
    let killSwitch = false;
    try {
      const body = (await response.json()) as { error?: string; killSwitch?: boolean };
      if (body.error) message = body.error;
      killSwitch = body.killSwitch === true;
    } catch {
      // Non-JSON error body; the status line will have to do.
    }
    throw new ApiError(message, response.status, killSwitch);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string): Promise<T> => request<T>(path),
  post: <T>(path: string, body?: unknown): Promise<T> =>
    request<T>(path, {
      method: 'POST',
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  patch: <T>(path: string, body: unknown): Promise<T> =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown): Promise<T> =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  delete: <T>(path: string): Promise<T> => request<T>(path, { method: 'DELETE' }),
};

// ---------------------------------------------------------------------------------------------
// Types mirroring the API responses.
// ---------------------------------------------------------------------------------------------

export interface EnvironmentBadge {
  environment: 'development' | 'production';
  label: string;
  sampleData: boolean;
  sampleDataLabel: string | null;
}

export interface Meta {
  badge: EnvironmentBadge;
  phase: number;
}

export interface User {
  id: string;
  email: string;
  name: string | null;
  roles: string[];
}

export interface Property {
  id: string;
  name: string;
  market_id: string;
  market_name: string;
  entity_id: string;
  entity_name: string;
  address_line1: string;
  address_line2: string | null;
  city: string;
  state: string;
  postal_code: string;
  bedrooms: number;
  bathrooms: string;
  sleeps: number;
  amenities: string[];
  status: string;
  notes: string | null;
  source_system: string;
  derivation: string;
  ingested_at: string;
  updated_at: string;
  approval_status: string;
}

export interface Market {
  id: string;
  slug: string;
  name: string;
  state: string;
}

/**
 * The open bookkeeping counts for one entity, computed in SQL with the entity list
 * (`GET /api/entities?counts=finance`). Shown beside the entity's name in the Finance picker.
 */
export interface EntityFinanceCounts {
  unclassified: number;
  rule_proposals: number;
  rule_conflicts: number;
  pending_ai_proposals: number;
  unresolved_anomalies: number;
}

export interface Entity {
  id: string;
  name: string;
  legal_name: string;
  jurisdiction: string;
  /**
   * The QuickBooks company file this entity keeps its books in, or null. Not a secret — Intuit
   * prints it in its own URLs — and it is what the Connections section on Settings lists a row
   * per (plan 10, Task 4).
   */
  qbo_realm_id: string | null;
  /** The Stripe account whose payouts land in this entity's bank, or null (Phase 9). */
  stripe_account_id: string | null;
  /** Present only when the list was asked for `?counts=finance`. */
  finance?: EntityFinanceCounts;
}

/**
 * One row of `GET /api/credentials`. SHAPE ONLY, and there is no shape that could carry a
 * secret: no ciphertext, no length, no prefix. The value the owner pasted cannot be read back by
 * anything — not this screen, not a session, not the API (SECURITY.md 7).
 */
export interface CredentialDescription {
  provider: string;
  accountRef: string;
  keyVersion: number;
  scopes: string[] | null;
  expiresAt: string | null;
  lastRefreshedAt: string | null;
  lastSyncedAt: string | null;
  syncWatermark: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * `GET /api/credentials`. The list, plus the one string the owner has to act on outside this
 * application: the redirect URI to register on the Intuit app (Keys & credentials -> Redirect
 * URIs). It is derived by the API from one function so that the authorize URL, the token
 * exchange, and what the owner registers cannot disagree - Intuit compares them exactly, and a
 * mismatch is an error message from Intuit that explains nothing. Not a secret.
 */
export interface ImportFreshness {
  provider: string;
  accountRef: string;
  lastSyncedAt: string | null;
  hoursSince: number | null;
  stale: boolean;
}

/** The two owner-facing SLOs (OPERATIONS.md §4): import freshness and the 7-day run failure rate. */
export interface ServiceLevels {
  importFreshness: ImportFreshness[];
  freshnessHours: number;
  agentRuns7d: { total: number; failed: number; failureRate: number; limit: number };
}

/** Whether the database has the schema this code expects (packages/db schemaCurrency). */
export interface SchemaCurrency {
  applied: number;
  total: number;
  pending: string[];
  drifted: string[];
}

export interface ConnectionsResponse {
  credentials: CredentialDescription[];
  redirectUri: string;
  slo: ServiceLevels;
  schema: SchemaCurrency;
}

export interface SixgenEvent {
  id: string;
  type: string;
  occurred_at: string;
  entity_table: string | null;
  entity_id: string | null;
  payload: Record<string, unknown>;
  caused_by_run_id: string | null;
  chain_depth: number;
}

export interface AuditEntry {
  id: string;
  occurred_at: string;
  actor_type: string;
  actor_id: string | null;
  action: string;
  target_table: string | null;
  target_id: string | null;
  run_id: string | null;
  environment: string;
}

export interface KillSwitchState {
  platformReadOnly: boolean;
  allAgentsDisabled: boolean;
  toolExecutionDisabled: boolean;
  autonomousActionsDisabled: boolean;
  disabledAgentIds: string[];
  disabledIntegrations: string[];
  updatedAt: string;
  reason: string | null;
}

/** One person who may sign in (or once could): /api/users. */
export interface UserJson {
  id: string;
  email: string;
  name: string | null;
  is_active: boolean;
  created_at: string;
  last_login_at: string | null;
  roles: string[];
}

export interface FeatureFlag {
  key: string;
  enabled: boolean;
  description: string;
  phase: number;
  updated_at: string;
}

// ---------------------------------------------------------------------------------------------
// Phase 2: the Control Center and the Approval Center.
// ---------------------------------------------------------------------------------------------

/**
 * One agent in the Control Center graph.
 *
 * `state` is computed by `derived.agent_status` and read straight through. Nothing in the client
 * derives, caches, or overrides it (CLAUDE.md 3.3), which is why there is no local status field
 * here to drift out of date.
 */
export interface AgentSummary {
  id: string;
  key: string;
  name: string;
  autonomyLevel: number;
  enabled: boolean;
  state: string;
  currentRunId: string | null;
  currentTask: string | null;
  currentTrigger: string | null;
  currentRunStartedAt: string | null;
  causedByRunId: string | null;
  causedByEventId: string | null;
  pendingApprovals: number;
}

export interface ControlCenter {
  environment: string;
  sampleData: boolean;
  killSwitch: {
    platformReadOnly: boolean;
    allAgentsDisabled: boolean;
    toolExecutionDisabled: boolean;
    disabledAgentIds: string[];
  };
  agents: AgentSummary[];
}

export interface ContextManifestEntry {
  scope: string;
  rowCount: number;
  truncated: boolean;
}

export interface RunDetail {
  run: {
    id: string;
    agent_key: string;
    agent_name: string;
    trigger: string;
    task: string;
    status: string;
    output: unknown;
    confidence: string | null;
    errors: { message: string } | null;
    started_at: string;
    ended_at: string | null;
    model: string | null;
    estimated_cost_cents: string | null;
  };
  timeline: {
    id: string;
    occurred_at: string;
    from_status: string | null;
    to_status: string;
    detail: string | null;
    context_manifest: ContextManifestEntry[] | null;
  }[];
  toolCalls: {
    id: string;
    tool_key: string;
    risk_class: string;
    permission_decision: string;
    permission_reason: string | null;
    result: unknown;
    error: string | null;
  }[];
  approvals: { id: string; tool_key: string; title: string; status: string }[];
}

/**
 * The decision explanation the owner reads. AGENTS.md 1.5.
 *
 * A conclusion, concrete reasons, the data it came from, and a confidence. Never the model's
 * chain of thought — that is not stored, so it cannot be displayed (CLAUDE.md 3.9).
 */
export interface Recommendation {
  action: string;
  why?: string[];
  data_sources?: string[];
  confidence?: string;
}

export interface ApprovalSummary {
  id: string;
  tool_key: string;
  risk_class: 'LOW' | 'MEDIUM' | 'HIGH';
  title: string;
  status: 'pending' | 'approved' | 'rejected';
  recommendation: Recommendation;
  was_modified: boolean;
  created_at: string;
  decided_at: string | null;
  executed_at: string | null;
  agent_key: string;
  agent_name: string;
  run_id: string;
  decided_by_name: string | null;
}

export interface ApprovalDetail extends ApprovalSummary {
  supporting_data: unknown;
  proposed_input: unknown;
  final_input: unknown;
  comment: string | null;
  autonomy_level: number;
}

// ---------------------------------------------------------------------------------------------
// Phase 3: the Finance screens. GET /api/finance/* (apps/api/src/routes/finance.ts).
//
// Every cents value is a STRING and stays one until `formatMoney` (money.ts) renders it. Dates
// (`txn_date`) are `YYYY-MM-DD` text. Everything else is shown as sent; the lineage panel exists
// to show these fields verbatim, not to summarise them (CLAUDE.md 13).
// ---------------------------------------------------------------------------------------------

/** `core.derivation`. */
export type Derivation = 'observed' | 'calculated' | 'ai_inferred' | 'human_entered';

/**
 * The approved (or otherwise) classification of a transaction, with its provenance: which of the
 * two writers produced it, from which rule or which model recommendation, and who approved it.
 */
export interface TransactionClassification {
  id: string;
  account_id: string | null;
  account_name: string | null;
  category: string | null;
  subcategory: string | null;
  property_id: string | null;
  property_name: string | null;
  is_capex: boolean;
  approval_status: 'not_required' | 'pending' | 'approved' | 'rejected';
  derivation: Derivation;
  confidence: string | null;
  from_ai_classification_id: string | null;
  from_vendor_rule_id: string | null;
  ai_run_id: string | null;
  approved_by: string | null;
  approved_by_name: string | null;
  approved_at: string | null;
  external_sync_status: string;
  /** The latest sync-back outcome in QuickBooks' words, or null when none has run (ADR-0007). */
  sync_detail: string | null;
  sync_at: string | null;
}

/** A vendor rule matched this line. Several rules is a conflict, which proposes nothing. */
export interface RuleHit {
  is_conflict: boolean;
  canonical_key: string | null;
  rule_id: string | null;
  rule_ids: string[];
  property_id: string | null;
  is_capex: boolean | null;
}

/** The Finance Agent's pending proposal for this line, waiting in the Approval Center. */
export interface PendingAiProposal {
  id: string;
  title: string;
  run_id: string;
  created_at: string;
}

export interface Transaction {
  id: string;
  entity_id: string;
  txn_date: string;
  amount_cents: string;
  currency: string;
  description: string | null;
  memo: string | null;
  doc_type: string | null;
  doc_number: string | null;
  doc_line_num: number | null;
  property_id: string | null;
  property_name: string | null;
  vendor_id: string | null;
  vendor_name: string | null;
  account_id: string | null;
  account_name: string | null;
  canonical_key: string | null;
  canonical_name: string | null;
  transfer_group_id: string | null;
  source_system: string;
  source_record_id: string | null;
  raw_id: string | null;
  ingested_at: string;
  updated_at: string;
  /** Set when the latest QuickBooks version of the document no longer carries this line (0038). */
  removed_at: string | null;
  derivation: Derivation;
  approval_status: string;
  duplicate_candidates: number;
  /**
   * What the deterministic detectors flagged this line for — `unusual_amount`, `new_vendor`,
   * `subscription` — sorted and distinct, empty when nothing. The kind is the whole verdict
   * (FINANCE.md 11): the screen shows these strings and adds no label or severity of its own.
   */
  anomaly_kinds: string[];
  classification: TransactionClassification | null;
  rule_hit: RuleHit | null;
  ai_proposal: PendingAiProposal | null;
}

export type TransactionStatusFilter = 'unclassified' | 'classified' | 'all';

export interface TransactionsPage {
  environment: string;
  sampleData: boolean;
  transactions: Transaction[];
  /** Opaque. Pass back as `cursor=` for the next page; null on the last page. */
  next_cursor: string | null;
}

// ---------------------------------------------------------------------------------------------
// Phase 3: the Bookkeeping queues. GET /api/finance/bookkeeping?entity= and the three writes
// behind the page's buttons. Every write replies with a per-row `results` list, never a verdict
// on the batch (plan 8a decision 3): the page shows each row its own outcome.
// ---------------------------------------------------------------------------------------------

/** A capped queue with its SQL-computed total. `total` may exceed `items.length` (the cap). */
export interface BookkeepingQueue<T> {
  total: number;
  items: T[];
}

/**
 * One transaction in `derived.rule_classification`. A proposal when exactly one active vendor
 * rule matches; a conflict when several do, in which case the proposal fields are null and the
 * competing rules and their canonical keys are listed instead.
 */
export interface RuleHitItem {
  transaction_id: string;
  txn_date: string;
  amount_cents: string;
  description: string | null;
  doc_type: string | null;
  doc_number: string | null;
  vendor_name: string | null;
  is_conflict: boolean;
  rule_ids: string[];
  canonical_key: string | null;
  canonical_name: string | null;
  rule_id: string | null;
  property_id: string | null;
  property_name: string | null;
  is_capex: boolean | null;
  /** Only on a conflict: one key per rule, in `rule_ids` order. */
  conflicting_canonical_keys: string[] | null;
}

/** The transaction an AI proposal or an anomaly is about, as the queue carries it. */
export interface QueueTransaction {
  id: string;
  txn_date: string;
  amount_cents: string;
  description: string | null;
  doc_type: string | null;
  doc_number: string | null;
  vendor_name: string | null;
}

/** A pending `propose_transaction_classification` approval on one of this entity's lines. */
export interface AiProposalItem {
  id: string;
  title: string;
  recommendation: Recommendation;
  proposed_input: unknown;
  created_at: string;
  run_id: string;
  agent_key: string;
  agent_name: string;
  transaction: QueueTransaction;
}

/** An unresolved `ai.anomaly`: a flag for a person, never a verdict (AGENTS.md 3). */
export interface AnomalyItem {
  id: string;
  transaction_id: string | null;
  kind: string;
  severity: 'low' | 'medium' | 'high';
  summary: string;
  detail: unknown;
  confidence_level: string | null;
  created_at: string;
  run_id: string | null;
  /** The transaction's fields, null when the anomaly is not about one line. */
  txn_date: string | null;
  amount_cents: string | null;
  description: string | null;
  vendor_name: string | null;
}

export interface BookkeepingQueues {
  environment: string;
  sampleData: boolean;
  /** Each queue holds at most this many items; its `total` is the real count. */
  cap: number;
  rule_hits: BookkeepingQueue<RuleHitItem> & { total_proposals: number; total_conflicts: number };
  ai_proposals: BookkeepingQueue<AiProposalItem>;
  anomalies: BookkeepingQueue<AnomalyItem>;
}

/**
 * `POST /api/bookkeeping/rule-hits/approve`. One row per transaction asked about.
 *
 * `refused` and `failed` are different words on purpose. A refusal is a rule saying no — reading
 * it is the point, and retrying changes nothing. A failure is something breaking, with nothing
 * recorded for that row; retrying may well work. The server keeps every row it decided before the
 * failure, so a partly-failed batch still reports what it did.
 */
export type RuleHitResult =
  | { transactionId: string; status: 'classified'; classificationId: string }
  | { transactionId: string; status: 'refused'; error: string }
  | { transactionId: string; status: 'failed'; error: string };

/**
 * `POST /api/approvals/decisions`. One row per approval asked about. `executed` is the only word
 * on whether the action ran; `status` alone says a decision was recorded. 404 no such approval;
 * 409 already decided; 502 approved and recorded, but the action did not run.
 */
export interface BulkDecisionResult {
  id: string;
  /** `failed`: the decision threw and nothing was recorded for this approval. 500. */
  status: 'approved' | 'rejected' | 'pending' | 'unknown' | 'failed';
  executed: boolean;
  wasModified: boolean;
  error?: string;
  httpStatus: 200 | 404 | 409 | 500 | 502;
}

// ---------------------------------------------------------------------------------------------
// Phase 3: the reconciliation report. GET /api/finance/reconciliation?entity=&months=. The shape
// of `packages/finance/src/reconcile.ts`'s `ReconciliationReport`, mirrored here as every other
// response is. Every `detail` is a finished sentence with its amounts already formatted; the
// three `*_cents` fields are strings for `formatMoney`. Nothing is computed from any of it.
// ---------------------------------------------------------------------------------------------

export type DifferenceKind =
  | 'documents'
  | 'amounts'
  | 'duplicates'
  | 'ambiguous_transfers'
  | 'unsupported_transfers'
  | 'unresolved_anomalies'
  | 'drift'
  | 'payouts'
  | 'anomalies';

/** `attention`: the mirror and QuickBooks disagree. `info`: open work for a person. */
export type DifferenceSeverity = 'info' | 'attention';

export interface Difference {
  kind: DifferenceKind;
  severity: DifferenceSeverity;
  detail: string;
}

export type PayoutMatchStatus = 'matched' | 'ambiguous' | 'unmatched';

/**
 * One Stripe payout on a month's card, from `derived.payout_match`. `amount_cents` and
 * `arrival_date` are text for the same reason every other amount and date is; `days_unmatched`
 * is the view's integer and is displayed, not compared.
 */
export interface MonthPayout {
  payout_id: string;
  stripe_payout_id: string;
  arrival_date: string;
  amount_cents: string;
  status: PayoutMatchStatus;
  /** The QuickBooks Deposit document key when `status` is `matched`; null otherwise. */
  deposit_source_record_id: string | null;
  /** Days since `arrival_date` when `status` is `unmatched`; null otherwise. */
  days_unmatched: number | null;
}

export interface MonthReport {
  /** The first of the month, `YYYY-MM-DD`. */
  month: string;
  raw_documents: number;
  core_documents: number;
  core_lines: number;
  raw_amount_cents: string;
  core_amount_cents: string;
  difference_cents: string;
  unclassified: number;
  classified: number;
  rule_proposals: number;
  rule_conflicts: number;
  duplicate_candidates: number;
  ambiguous_transfers: number;
  unsupported_transfers: number;
  paired_transfers: number;
  unresolved_anomalies: number;
  drift: number;
  payouts_unmatched: number;
  payouts_ambiguous: number;
  anomaly_unusual_amount: number;
  anomaly_new_vendor: number;
  anomaly_subscription: number;
  /** The month's payouts by `arrival_date`, oldest first, whatever their status. */
  payouts: MonthPayout[];
  /** `attention` first, then `info`. */
  differences: Difference[];
  reconciled: boolean;
}

export interface ReconciliationResponse {
  environment: string;
  sampleData: boolean;
  as_of: string;
  months: number;
  /** One entry for the requested entity, when it has any month on the view. Oldest month first. */
  entities: { entity_id: string; months: MonthReport[] }[];
  orphan_anomalies: number;
  bank: { status: 'not_connected' };
}

// ---------------------------------------------------------------------------------------------
// Reservations and revenue. Phase 4, plan 21.
//
// Every `string | null` field below that names money is INTEGER CENTS AS A STRING, and the only
// thing the browser may do with it is hand it to `formatMoney`. Every ratio is a FRACTION — 0.45,
// not 45 — because a percentage is a presentation of a fraction and the fraction is the figure the
// finance engine computed.
// ---------------------------------------------------------------------------------------------

export interface Channel {
  key: string;
  name: string;
}

export interface Reservation {
  id: string;
  propertyId: string;
  propertyName: string;
  entityId: string;
  channel: string;
  confirmationCode: string | null;
  status: string;
  /** The database's own answer to "is this a real stay", from a GENERATED column. */
  countsAsBooked: boolean;
  checkIn: string;
  checkOut: string;
  nights: number;
  /** When GUESTY recorded the booking, which is not when it was imported. Null when unknown. */
  bookedAt: string | null;
  cancelledAt: string | null;
  adults: number | null;
  children: number | null;
  accommodation: string;
  cleaningFee: string;
  taxes: string;
  hostPayout: string;
  totalPaid: string;
  currency: string;
  lineage: { sourceSystem: string; sourceRecordId: string; ingestedAt: string };
}

export interface ReservationsResponse {
  reservations: Reservation[];
  total: number;
  limit: number;
  offset: number;
}

export interface DateWindow {
  from: string;
  to: string;
}

export interface RevenueWindows {
  asOf: string;
  trailing: DateWindow;
  trailingLastYear: DateWindow;
  forward: DateWindow;
  forwardLastYear: DateWindow;
  forwardLastYearAsOf: string;
}

export interface PortfolioPeriod {
  properties: number;
  bookedNights: number;
  availableNights: number;
  blockedNights: number;
  /** Booked plus available. Blocked nights are deliberately NOT in occupancy's denominator. */
  sellableNights: number;
  occupancy: number | null;
  adr: string | null;
  revpar: string | null;
  roomRevenue: string | null;
  /** How the figures were arrived at, carried with them so the screen can show it. */
  calcMethod: string;
}

export interface ChannelShare {
  channel: string;
  bookings: number;
  roomRevenue: string | null;
  bookingShare: number | null;
  revenueShare: number | null;
}

export interface PortfolioResponse {
  windows: RevenueWindows;
  trailing: PortfolioPeriod;
  trailingLastYear: PortfolioPeriod;
  channelMix: ChannelShare[];
}

export interface PropertyPerformance {
  propertyId: string;
  propertyName: string;
  entityId: string;
  trailing: {
    bookedNights: number;
    availableNights: number;
    blockedNights: number;
    sellableNights: number;
    occupancy: number | null;
    adr: string | null;
    revpar: string | null;
    roomRevenue: string | null;
  };
  trailingLastYear: {
    bookedNights: number;
    sellableNights: number;
    occupancy: number | null;
    adr: string | null;
    revpar: string | null;
    roomRevenue: string | null;
  };
  forward: { nights: number; roomRevenue: string | null };
  forwardLastYear: { nights: number; roomRevenue: string | null };
  /** This year's forward book against last year's, both read at the same lead time. */
  pace: { nightsRatio: number | null; revenueRatio: number | null; leadDays: number };
}

export interface RevenuePropertiesResponse {
  windows: RevenueWindows;
  properties: PropertyPerformance[];
}

/**
 * `GET /api/finance/pnl`. Every figure is ACTUAL; `pending_*` is the money nobody has classified,
 * beside the statement and never inside it. Cents are decimal strings (CLAUDE.md 3.1).
 */
export interface PnlFigures {
  label: string;
  lines: Array<{ key: string; section: string; amount_cents: string; line_count: number }>;
  sections: Record<string, string>;
  operating_revenue_cents: string;
  operating_expense_cents: string;
  noi_cents: string;
  noi_margin: number | null;
  free_cash_flow_cents: string;
  net_cents: string;
  pending_cents: string;
  pending_line_count: number;
  pending_label: string;
  complete: boolean;
  calc_method: string;
}

/** The cash flow statement over the same rows: operating, investing, financing, net change. */
export interface CashFlowFigures {
  label: string;
  operating_cents: string;
  investing_cents: string;
  financing_cents: string;
  net_change_cents: string;
  /** Movements between the entity's own balance-sheet accounts, outside the net change. */
  balance_sheet_cents: string;
  pending_cents: string;
  pending_line_count: number;
  complete: boolean;
  calc_method: string;
}

export interface PnlResponse {
  environment: string;
  sampleData: boolean;
  as_of: string;
  entity_id: string;
  property_id: string | null;
  from: string;
  to: string;
  months: Array<{ month: string; pnl: PnlFigures; cash_flow: CashFlowFigures }>;
  total: PnlFigures;
  cash_flow_total: CashFlowFigures;
}

/** A `core.loan` row as `GET /api/loans` returns it; cents are decimal strings. */
export interface Loan {
  id: string;
  entity_id: string;
  property_id: string | null;
  lender: string;
  name: string;
  principal_cents: string;
  annual_rate_bps: number;
  term_months: number;
  payment_cents: string | null;
  first_payment_date: string;
  rate_type: 'fixed' | 'variable';
  escrow_cents: string;
  status: 'active' | 'paid_off';
  notes: string | null;
  updated_at: string;
}

/** What the schedule says about a loan as of a date: computed, never stored. */
export interface LoanPosition {
  as_of: string;
  payment_cents: string;
  balance_cents: string;
  maturity_date: string;
  balloon_cents: string;
  total_interest_cents: string;
  next_payment: {
    date: string;
    payment_cents: string;
    interest_cents: string;
    principal_cents: string;
  } | null;
  paid_off: boolean;
}

export interface LoansResponse {
  environment: string;
  sampleData: boolean;
  as_of: string;
  loans: Array<{ loan: Loan; position: LoanPosition }>;
}

export interface LoanScheduleRow {
  period: number;
  date: string;
  opening_balance_cents: string;
  payment_cents: string;
  interest_cents: string;
  principal_cents: string;
  closing_balance_cents: string;
}

export interface LoanScheduleResponse {
  loan: Loan;
  position: LoanPosition;
  rows: LoanScheduleRow[];
  calc_method: string;
}

/** `GET /api/finance/cash`: the ledger's balances summed, and the loans' debt service ahead. */
export interface CashResponse {
  environment: string;
  sampleData: boolean;
  as_of: string;
  entity_id: string;
  accounts: Array<{
    account_id: string;
    name: string;
    account_subtype: string | null;
    kind: 'bank' | 'credit_card';
    current_balance_cents: string | null;
    balance_as_of: string | null;
    is_active: boolean;
  }>;
  position: {
    label: string;
    cash_cents: string;
    credit_card_cents: string;
    net_cents: string;
    bank_accounts: number;
    credit_cards: number;
    unknown_balances: number;
    calc_method: string;
  };
  debt_service: Array<{
    days: number;
    from: string;
    to: string;
    payment_count: number;
    principal_cents: string;
    interest_cents: string;
    escrow_cents: string;
    total_cents: string;
    /** Net cash less this horizon's debt service: computed by the API, not the screen. */
    cash_after_cents: string;
  }>;
  active_loans: number;
  /** FORECAST: opening + confirmed and expected payouts - debt service - operating - CapEx. */
  forecast: {
    label: string;
    calc_method: string;
    operating_monthly_cents: string;
    trailing_months: number;
    bookings_considered: number;
    /** Prior-year stays read for seasonality and pace; 0 means no prior year, nothing expected. */
    prior_year_stays_considered: number;
    capex_considered: number;
    maintenance_considered: number;
    horizons: Array<{
      days: number;
      from: string;
      to: string;
      opening_cents: string;
      /** Confirmed plus expected. */
      inflows_cents: string;
      confirmed_cents: string;
      booking_count: number;
      /** Last year's late bookings for this window, at this year's pace. */
      expected_cents: string;
      prior_year_final_cents: string;
      prior_year_on_books_cents: string;
      /** Basis points, clamped 5,000–15,000; null when last year had nothing on the books. */
      pace_bps: string | null;
      debt_service_cents: string;
      operating_cents: string;
      /** Planned CapEx dated inside the horizon, at budget. */
      capex_cents: string;
      capex_count: number;
      maintenance_cents: string;
      maintenance_count: number;
      base_cents: string;
      upside_cents: string;
      downside_cents: string;
    }>;
  };
  /** The entity's cash floor and the forecast checked against it; null until one is set. */
  reserve: {
    policy: { minimum_cash_cents: string; notes: string | null; updated_at: string };
    check: {
      opening_breached: boolean;
      breached: boolean;
      first: { days: number; to: string; base_cents: string; shortfall_cents: string } | null;
      breached_days: number[];
      calc_method: string;
    };
  } | null;
}

/** `GET /api/finance/monthly-review`: one checklist per month, ready exactly when nothing is open. */
export interface MonthlyReviewResponse {
  environment: string;
  sampleData: boolean;
  as_of: string;
  entity_id: string;
  months: Array<{
    month: string;
    items: Array<{ key: string; label: string; screen: string; count: number }>;
    open: Array<{ key: string; label: string; screen: string; count: number }>;
    openCount: number;
    ready: boolean;
    calcMethod: string;
    /** The money on lines whose account is not on the chart, this month. */
    unmapped_cents: string;
  }>;
}

/** One thing the Finance Agent said, about one entity: the words beside the figures. */
export interface NarrationItemJson {
  kind: 'close_readiness' | 'variance' | 'cash';
  /** `YYYY-MM` for close_readiness and variance; null for cash. */
  month: string | null;
  line_key: string | null;
  open_item_keys: string[] | null;
  direction: 'favourable' | 'unfavourable' | 'on_budget' | null;
  floor_breached: boolean | null;
  title: string;
  narrative: string;
  why: string[];
  data_sources: string[];
  severity: 'high' | 'medium' | 'low';
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
}

/** `GET /api/finance/narration?entity`: the latest narration run's items for one entity. */
export interface FinanceNarrationResponse {
  environment: string;
  sampleData: boolean;
  entity_id: string;
  /** Null until the Finance Agent has completed a narration run. */
  narration: {
    run_id: string;
    generated_at: string;
    model: string | null;
    trigger: string;
    confidence: string;
    summary: string;
    checked_and_quiet: string[];
    items: NarrationItemJson[];
  } | null;
}

/** `GET /api/finance/forecast-backtest`: the forecast re-run from past month-starts, inflows and cash legs. */
export interface ForecastBacktestResponse {
  environment: string;
  sampleData: boolean;
  entity_id: string;
  as_of: string;
  months: number;
  /** The month-starts re-run, oldest first: those whose 90 days had elapsed by as-of. */
  as_of_dates: string[];
  stays_considered: number;
  label: string;
  calc_method: string;
  snapshots: Array<{
    as_of: string;
    days: number;
    confirmed_cents: string;
    expected_cents: string;
    forecast_cents: string;
    actual_cents: string;
    /** Forecast less actual: positive ran high. */
    error_cents: string;
    abs_error_cents: string;
    /** What the confirmed line alone would have missed by. */
    confirmed_error_cents: string;
    error_ratio: number | null;
  }>;
  horizons: Array<{
    days: number;
    snapshots: number;
    mean_abs_error_cents: string;
    mean_error_cents: string;
    mean_abs_pct_error: number | null;
    confirmed_mean_abs_error_cents: string;
  }>;
  /** The cash leg: closing cash forecast against the position that held (0074's history). */
  cash: {
    label: string;
    calc_method: string;
    /** When the earliest balance version was seen; null when there is no history at all. */
    history_starts_at: string | null;
    balance_versions: number;
    /** Date/horizon pairs the history did not cover: counted, not guessed. */
    uncovered_snapshots: number;
    snapshots: Array<{
      as_of: string;
      days: number;
      opening_cents: string;
      forecast_cents: string;
      actual_cents: string;
      error_cents: string;
      abs_error_cents: string;
      /** What "nothing changes" (closing = opening) would have missed by. */
      naive_error_cents: string;
    }>;
    horizons: Array<{
      days: number;
      snapshots: number;
      mean_abs_error_cents: string;
      mean_error_cents: string;
      naive_mean_abs_error_cents: string;
    }>;
  };
}

/** `GET /api/finance/overview`: the dashboard's questions, one row per entity (FINANCE.md §8.2). */
export interface FinanceOverviewResponse {
  environment: string;
  sampleData: boolean;
  as_of: string;
  /** The last completed month, `YYYY-MM`. */
  month: string;
  calc_method: string;
  totals: {
    net_cash_cents: string;
    revenue_cents: string;
    expenses_cents: string;
    noi_cents: string;
    free_cash_flow_cents: string;
    base_90_cents: string;
    attention: number;
  };
  /** The Finance Agent's latest narration, summary only; null until it has run. */
  narration: { run_id: string; generated_at: string; summary: string } | null;
  entities: Array<{
    entity_id: string;
    entity_name: string;
    net_cash_cents: string;
    bank_accounts: number;
    unknown_balances: number;
    base_30_cents: string;
    base_60_cents: string;
    base_90_cents: string;
    floor_cents: string | null;
    floor_breached: boolean | null;
    revenue_cents: string;
    expenses_cents: string;
    noi_cents: string;
    free_cash_flow_cents: string;
    /** Lines on an account not on the chart: in no sum. */
    pending_lines: number;
    /** Null when the month has no lines at all. */
    month_ready: boolean | null;
    open_items: number;
    noi_budget_cents: string | null;
    noi_variance_cents: string | null;
    narration_high_items: string[];
    attention: number;
  }>;
}

/** One property's (or the unattributed) figures over a range of months. */
export interface PropertyFinanceRow {
  /** Null for the lines on no property. */
  property_id: string | null;
  name: string;
  market_name: string | null;
  revenue_cents: string;
  expenses_cents: string;
  noi_cents: string;
  pending_lines: number;
  booked_nights: number | null;
  available_nights: number | null;
  blocked_nights: number | null;
  room_revenue_cents: string | null;
  /** Booked ÷ sellable; null with nothing on the calendar. */
  occupancy: number | null;
  adr_cents: string | null;
  revpar_cents: string | null;
}

/** `GET /api/finance/properties`: NOI per property beside its nights (FINANCE.md §8.2). */
export interface PropertyFinanceResponse {
  environment: string;
  sampleData: boolean;
  entity_id: string;
  from: string;
  to: string;
  calc_method: string;
  properties: PropertyFinanceRow[];
  unattributed: PropertyFinanceRow;
  total: {
    revenue_cents: string;
    expenses_cents: string;
    noi_cents: string;
    pending_lines: number;
    booked_nights: number;
    available_nights: number;
    occupancy: number | null;
    adr_cents: string | null;
    revpar_cents: string | null;
  };
}

/** `GET /api/finance/balance-sheet`: the ledger's balance-sheet accounts, totalled. */
/** `GET /api/finance/tie-out` — Sixgen's P&L against QuickBooks' own report for one month. */
export interface TieOutResponse {
  environment: string;
  sampleData: boolean;
  entity: { id: string; name: string; realmId: string | null };
  month: string;
  basis: 'Accrual' | 'Cash';
  window: { startDate: string; endDate: string };
  /** The stored QuickBooks report compared against; null when none is stored for the month. */
  report: { id: string; fetchedAt: string; basis: string; currency: string } | null;
  /** QuickBooks' document counts per transaction type for the window; null until fetched. */
  census: {
    fetchedAt: string;
    counts: Record<string, number>;
    imported: string[];
    notImported: Array<{ type: string; count: number }>;
  } | null;
  tieOut: {
    label: string;
    calcMethod: string;
    status: TieOutStatus;
    counts: Record<TieOutStatus, number>;
    reportedNetIncomeCents: string;
    oursNetIncomeCents: string;
    netDifferenceCents: string;
    unassigned: { signedCents: string; lineCount: number };
    lines: Array<{
      accountId: string;
      name: string;
      section: string;
      /** All three in the report's sense: income positive, expense positive. */
      reportedCents: string;
      oursCents: string;
      mirrorCents: string;
      differenceCents: string;
      status: TieOutStatus;
      reasons: Array<'transfers_excluded' | 'reclassified_not_in_quickbooks'>;
      lineCount: number;
    }>;
  } | null;
}
export type TieOutStatus = 'tied' | 'explained' | 'unexplained';

export interface BalanceSheetResponse {
  environment: string;
  sampleData: boolean;
  entity_id: string;
  balances_as_of: string | null;
  lines: Array<{
    account_id: string;
    name: string;
    account_type: 'asset' | 'liability' | 'equity';
    account_subtype: string | null;
    canonical_key: string | null;
    /** As QuickBooks reported it: debit-positive, so a liability owed is negative. */
    current_balance_cents: string | null;
    /** As a person reads a sheet: liabilities owed and equity contributed positive. */
    display_cents: string | null;
    balance_as_of: string | null;
    is_active: boolean;
  }>;
  sheet: {
    label: string;
    assets_cents: string;
    liabilities_cents: string;
    equity_cents: string;
    /** Assets less liabilities less equity: QuickBooks' unclosed net income, or an imbalance. */
    unclosed_cents: string;
    balanced: boolean;
    accounts: number;
    unknown_balances: number;
    calc_method: string;
  };
}

/** `GET /api/finance/budget`: actual against budget per canonical line, per month and in total. */
export interface VarianceFigures {
  label: string;
  lines: Array<{
    key: string;
    section: string;
    actual_cents: string;
    budget_cents: string;
    /** Actual less budget: positive is favourable for every line. */
    variance_cents: string;
    variance_ratio: number | null;
    has_budget: boolean;
    has_actual: boolean;
  }>;
  sections: Array<{
    section: string;
    actual_cents: string;
    budget_cents: string;
    variance_cents: string;
  }>;
  noi: { actual_cents: string; budget_cents: string; variance_cents: string };
  unbudgeted_lines: number;
  unspent_lines: number;
  calc_method: string;
}

export interface BudgetResponse {
  environment: string;
  sampleData: boolean;
  scope: 'entity' | 'portfolio' | 'market';
  entity_id: string | null;
  market_id: string | null;
  property_id: string | null;
  /** What a portfolio or market comparison is made of; null for an entity. */
  scope_note: string | null;
  from: string;
  to: string;
  months: Array<{ month: string; variance: VarianceFigures }>;
  total: VarianceFigures;
  budget_lines: Array<{
    id: string;
    month: string;
    canonical_key: string;
    statement_section: string;
    amount_cents: string;
    notes: string | null;
  }>;
  canonical_keys: Array<{ key: string; name: string; statement_section: string }>;
}

/** A `core.capex_item` row as `GET /api/capex` returns it; cents are decimal strings. */
export interface CapexItem {
  id: string;
  entity_id: string;
  property_id: string | null;
  name: string;
  vendor: string | null;
  planned_on: string;
  budget_cents: string;
  actual_cost_cents: string | null;
  completed_on: string | null;
  estimated_annual_revenue_impact_cents: string | null;
  status: 'planned' | 'in_progress' | 'complete' | 'cancelled';
  notes: string | null;
  updated_at: string;
}

export interface CapexResponse {
  environment: string;
  sampleData: boolean;
  items: Array<{
    item: CapexItem;
    /** The actual cost where known, else the budget. */
    cost_cents: string;
    cost_is_actual: boolean;
    /** Estimated annual revenue impact over the cost, or null until an impact is estimated. */
    expected_roi: number | null;
  }>;
}

// ---------------------------------------------------------------------------------------------
// Knowledge (Phase 6 groundwork): search, sources, documents, policies, episodes.
// ---------------------------------------------------------------------------------------------

export interface KnowledgeSourceJson {
  id: string;
  kind: 'drive' | 'gmail' | 'upload' | 'mock';
  key: string;
  name: string;
  config: Record<string, unknown>;
  is_enabled: boolean;
  updated_at: string;
}

export interface KnowledgeDocumentJson {
  id: string;
  source_key: string;
  source_kind: KnowledgeSourceJson['kind'];
  external_id: string;
  title: string;
  mime_type: string | null;
  url: string | null;
  modified_at: string | null;
  /** Who may see it in the source system; an empty mirror means everyone signed in. */
  acl: { emails: string[]; domains: string[]; anyone: boolean };
  chunk_count: number;
  indexed_at: string | null;
  removed_at: string | null;
}

/** `GET /api/knowledge/search?q`: hybrid retrieval as the viewer, every hit scored and sourced. */
export interface KnowledgeSearchResponse {
  environment: string;
  sampleData: boolean;
  query: string;
  viewer: string;
  /** The model the question was embedded with; null means full text only. */
  embedding_model: string | null;
  calc_method: string;
  hits: Array<{
    chunk_id: string;
    document_id: string;
    title: string;
    url: string | null;
    source_key: string;
    source_kind: KnowledgeSourceJson['kind'];
    ordinal: number;
    text: string;
    fts_rank: number;
    cosine: number | null;
    recency: number;
    score: number;
  }>;
}

export interface PolicyJson {
  id: string;
  key: string;
  title: string;
  body: string;
  category: string;
  version: number;
  is_active: boolean;
  has_embedding: boolean;
  embedding_model: string | null;
  updated_at: string;
}

export interface PolicyRevisionJson {
  id: string;
  version: number;
  title: string;
  body: string;
  category: string;
  edited_by: string | null;
  edited_at: string;
}

export interface EpisodeJson {
  id: string;
  kind: string;
  title: string;
  summary: string;
  occurred_on: string;
  ended_on: string | null;
  entity_id: string | null;
  property_id: string | null;
  links: Array<{ kind: string; id: string }>;
  status: 'proposed' | 'confirmed' | 'rejected';
  proposed_by_run_id: string | null;
  decided_by: string | null;
  decided_at: string | null;
  derivation: string;
  has_embedding: boolean;
  updated_at: string;
}

// ---------------------------------------------------------------------------------------------
// The command center (ARCHITECTURE.md §12, Phase 6 groundwork).
// ---------------------------------------------------------------------------------------------

/** One question's run as `GET /api/ask/:requestId` presents it; QUEUED until the worker's run exists. */
export interface AskJson {
  request_id: string | null;
  run_id?: string;
  status: 'QUEUED' | 'RUNNING' | 'COMPLETED' | 'FAILED';
  question?: string;
  viewer?: string;
  asked_at?: string;
  ended_at?: string | null;
  model?: string | null;
  confidence?: 'HIGH' | 'MEDIUM' | 'LOW' | null;
  answer?: string | null;
  citations?: Array<{ query: string; used_for: string }>;
  /** The queries the run chose, the SQL (or the code path) each ran, and the rows the model saw. */
  queries?: Array<{
    key: string;
    params: Record<string, unknown>;
    source: string;
    rows: Record<string, unknown>[];
    truncated: boolean;
  }>;
  note?: string | null;
  error?: string | null;
}

export interface AskResponse {
  environment: string;
  sampleData: boolean;
  ask: AskJson;
}

export interface AskListResponse {
  environment: string;
  sampleData: boolean;
  asks: AskJson[];
}

// ---------------------------------------------------------------------------------------------
// Operations and maintenance (migration 0057, Phase 7 groundwork).
// ---------------------------------------------------------------------------------------------

export type TicketSeverity = 'critical' | 'high' | 'medium' | 'low';
export type TicketStatus = 'open' | 'in_progress' | 'waiting_vendor' | 'resolved' | 'cancelled';

export interface TicketJson {
  id: string;
  property_id: string;
  property_name: string;
  entity_id: string;
  reservation_id: string | null;
  vendor_id: string | null;
  vendor_name: string | null;
  title: string;
  description: string;
  category: string;
  severity: TicketSeverity | null;
  status: TicketStatus;
  reported_at: string;
  due_at: string | null;
  resolved_at: string | null;
  resolution: string | null;
  estimated_cost_cents: string | null;
  actual_cost_cents: string | null;
  /** From derived.maintenance_ticket_age; null once the ticket is closed. */
  age_hours: number | null;
  is_overdue: boolean;
}

export interface TicketsResponse {
  environment: string;
  sampleData: boolean;
  tickets: TicketJson[];
  calc_method: string | null;
}

export interface RecurrenceJson {
  property_id: string;
  property_name: string;
  entity_id: string;
  category: string;
  ticket_count: number;
  first_reported_at: string;
  last_reported_at: string;
  ticket_ids: string[];
  titles: string[];
  calc_method: string;
}

export interface OpsTaskJson {
  id: string;
  property_id: string;
  property_name: string;
  entity_id: string;
  reservation_id: string | null;
  kind: 'turnover' | 'inspection' | 'supplies' | 'other';
  title: string;
  due_at: string;
  status: 'open' | 'done' | 'skipped';
  assignee: string | null;
  completed_at: string | null;
  notes: string | null;
}

export interface ReadinessJson {
  reservation_id: string;
  property_id: string;
  property_name: string;
  entity_id: string;
  check_in: string;
  check_out: string;
  days_until_check_in: number;
  open_turnover_tasks: number;
  blocking_tickets: number;
  other_open_tickets: number;
  ready: boolean;
  calc_method: string;
}

export interface PreventiveScheduleJson {
  id: string;
  property_id: string | null;
  property_name: string | null;
  category: string;
  title: string;
  interval_days: number;
  last_done_on: string | null;
  is_active: boolean;
  notes: string | null;
  next_due_on: string | null;
  days_until_due: number | null;
}

/** The Ops & Maintenance agent's latest call on an open ticket (ai.ticket_assessment). */
export interface TicketAssessmentJson {
  ticket_id: string;
  severity: TicketSeverity;
  guest_impact: boolean;
  escalate: boolean;
  why: string[];
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  ai_run_id: string;
  model: string | null;
  created_at: string;
}

/** The agent's latest completed review, from its run record. */
export interface OpsReviewJson {
  run_id: string;
  generated_at: string;
  model: string | null;
  trigger: string;
  summary: string;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  readiness_risks: Array<{
    reservationId: string;
    propertyId: string;
    openTurnoverTasks: number;
    blockingTickets: number;
    recommendation: string;
    dataSources: string[];
  }>;
  recurrences: Array<{
    propertyId: string;
    category: string;
    ticketCount: number;
    finding: string;
    recommendation: string;
    dataSources: string[];
  }>;
  preventive_overdue: Array<{
    scheduleId: string;
    daysOverdue: number;
    recommendation: string;
    dataSources: string[];
  }>;
  checked_and_quiet: string[];
}

// ---------------------------------------------------------------------------------------------
// Guest conversations (migration 0060, Phase 8 groundwork).
// ---------------------------------------------------------------------------------------------

export interface MessageTriageJson {
  id: string;
  message_id: string;
  sentiment: 'positive' | 'neutral' | 'negative';
  urgency: 'now' | 'today' | 'this_week' | 'none';
  is_complaint: boolean;
  refund_risk: 'high' | 'medium' | 'low' | 'none';
  review_risk: 'high' | 'medium' | 'low' | 'none';
  issue_category: string;
  summary: string;
  why: string[];
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  ai_run_id: string;
  model: string | null;
  created_at: string;
}

export interface GuestThreadJson {
  external_thread_id: string;
  reservation_id: string | null;
  guest_id: string | null;
  guest_name: string | null;
  property_id: string | null;
  property_name: string | null;
  entity_id: string | null;
  check_in: string | null;
  check_out: string | null;
  reservation_status: string | null;
  message_count: number;
  last_message_at: string | null;
  last_inbound_at: string | null;
  last_outbound_at: string | null;
  awaiting_reply: boolean;
  hours_waiting: number | null;
  latest_triage: MessageTriageJson | null;
}

export interface GuestMessageJson {
  id: string;
  external_thread_id: string;
  reservation_id: string | null;
  direction: 'inbound' | 'outbound';
  sender: 'guest' | 'host' | 'system' | 'agent';
  body: string;
  sent_at: string | null;
  status: 'received' | 'draft' | 'approved' | 'rejected' | 'sent';
  ai_run_id: string | null;
  triage: MessageTriageJson | null;
}

/** One grant row of derived.autonomy_report (Phase 12 groundwork, 0062). */
export interface AutonomyReportRowJson {
  grant_id: string;
  agent_id: string;
  agent_key: string;
  agent_version: number;
  autonomy_level: number;
  tool_key: string;
  enabled: boolean;
  demoted_at: string | null;
  demoted_reason: string | null;
  required_suite: string;
  required_consecutive_runs: number;
  min_confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  daily_cap: number;
  max_error_rate: string;
  error_window_days: number;
  executions_today: number;
  executions_window: number;
  errors_window: number;
  error_rate_window: string;
  eval_runs_considered: number;
  eval_prerequisite_met: boolean;
  last_eval_at: string | null;
  prerequisites_met: boolean;
  calc_method: string;
  as_of: string;
}

export interface AutonomyReportJson {
  report: AutonomyReportRowJson[];
  platform: { flag: string; flagEnabled: boolean; autonomousActionsDisabled: boolean };
  calc_method: string | null;
  as_of: string;
}

export interface AutonomyGrantJson {
  id: string;
  agent_id: string;
  tool_key: string;
  enabled: boolean;
  required_suite: string;
  required_consecutive_runs: number;
  min_confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  daily_cap: number;
  max_error_rate: string;
  error_window_days: number;
  demoted_at: string | null;
  demoted_reason: string | null;
}

export interface GuestThreadDetailJson {
  environment: string;
  sampleData: boolean;
  thread_id: string;
  messages: GuestMessageJson[];
  pending_drafts: Array<{
    approval_id: string;
    title: string;
    body: string;
    recommendation: unknown;
    run_id: string;
    created_at: string;
  }>;
}
